import ConfirmPopover from '@/components/common/ConfirmPopover';
import List from '@/components/common/List';
import ListArrows from '@/components/common/ListArrows';
import TextFieldWrapper from '@/components/common/TextFieldWrapper';
import PromptListPromptPickerDialog from '@/components/nodes/settings/prompt/PromptListPromptPickerDialog';
import { useApplication } from '@/hooks/useApplication';
import { ConfigurationVariable } from '@/types/metadata/metadata';
import { PromptId, PromptList } from '@/types/prompt';
import { KeyValue } from '@/types/variable';
import { isArray, isNumber, isObject, isVariable } from '@/util/typeGuards';
import { kvValueListUtil } from '@/util/validation/kvvaluelist';
import { Clear } from '@mui/icons-material';
import { Box, Card, ListItem, ListItemText, Stack, useTheme } from '@mui/material';
import Button from '@mui/material/Button';
import { FC, JSX, useState } from 'react';
import { ParamProps } from './ParamRenderer';
import ParamStringInteger from './ParamStringInteger';

const ParamKvValueList: FC<ParamProps> = (props) => {
  const theme = useTheme();
  const { application } = useApplication();

  const configuration = props.configuration as ConfigurationVariable;
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [openDialog, setOpenDialog] = useState(false);
  const initialValue = kvValueListUtil().getValue(props.configuration, props.initialValue) as
    | KeyValue<number | PromptList | undefined | (number | PromptList)[]>[]
    | undefined;

  const removeEntry = (
    indexToRemove: number,
    values: KeyValue<PromptId | PromptList | undefined | (PromptId | PromptList)[]>[]
  ) => {
    const newInitialValue = values.filter((_, index) => index !== indexToRemove);
    props.onChange([props.configuration.value, newInitialValue]);
  };

  const removeEntryFromArray = (key: string, valueToRemove: PromptId | PromptList) => {
    const newInitialValue = [] as KeyValue<PromptId | PromptList | undefined | (PromptId | PromptList)[]>[];
    initialValue?.forEach((initialValueArray) => {
      if (initialValueArray.key === key) {
        const innerValue = [] as (PromptId | PromptList)[];
        isArray(initialValueArray.value) &&
          initialValueArray.value.forEach((value) => {
            if (isNumber(valueToRemove)) {
              if (value !== valueToRemove) {
                innerValue.push(value);
              }
            } else if (isObject(valueToRemove)) {
              if (!isObject(value) || value.name !== valueToRemove.name) {
                innerValue.push(value);
              }
            }
          });
        newInitialValue.push({ key: initialValueArray.key, value: innerValue });
      } else {
        newInitialValue.push(initialValueArray);
      }
    });

    props.onChange([props.configuration.value, newInitialValue]);
  };

  const getPrompts = (kvObject: { key: string; value: (PromptId | PromptList)[] }, index: number) => {
    const result: JSX.Element[] = [];
    kvObject.value?.forEach((value, promptIndex) => {
      const isPrompt = isNumber(value);
      const selectedPrompt = isPrompt ? application?.prompts.find((prompt) => prompt.id === value) : undefined;
      const itemText = isNumber(value) ? selectedPrompt?.label || selectedPrompt?.file || '' : value?.name;
      {
        value &&
          result.push(
            <Box display={'flex'} p={1} pl={2} pr={2} key={`prompt.${kvObject.key}.${index}.${promptIndex}`}>
              <ListItemText
                primary={
                  <Stack direction='row' justifyContent='space-between' alignItems='center' pr={1}>
                    {selectedPrompt?.promptGroup
                      ? `${application.promptGroups.find((group) => group.id === selectedPrompt?.promptGroup)?.name} / `
                      : ''}
                    {itemText}
                    <code>{isPrompt ? 'prompt' : 'promptlist'}</code>
                  </Stack>
                }
              />
              <ConfirmPopover
                onConfirm={() => removeEntryFromArray(kvObject.key, value)}
                icon={<Clear />}
                text={
                  <>
                    Remove <code>{itemText}</code> from list?
                  </>
                }
              />
            </Box>
          );
      }
    });

    return (
      <Card
        variant='outlined'
        sx={{
          flexGrow: kvObject.value ? 1 : 0,
          textAlign: 'center'
        }}
      >
        {result}
        <Button
          variant='contained'
          color='primary'
          sx={{ m: 1 }}
          onClick={() => {
            setCurrentIndex(index);
            setOpenDialog(true);
          }}
        >
          Add
        </Button>
      </Card>
    );
  };

  return (
    <List
      subheader={configuration.value + (props.configuration.required ? ' *' : '')}
      {...(!!props.errors?.length && {
        subheaderSx: { color: theme.palette.error.main }
      })}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'start',
        width: '100%'
      }}
    >
      {initialValue?.map((kvObject, index) => (
        <ListItem key={`varlist.${index}`}>
          <TextFieldWrapper
            size='small'
            label={'key'}
            value={kvObject.key}
            onChange={(e) => {
              initialValue[index].key = e.currentTarget.value;
              props.onChange([configuration.value, initialValue]);
            }}
            sx={{ width: 100, mr: 2 }}
          />
          {configuration.subtype === 'prompts' ? (
            isArray(kvObject.value) && getPrompts(kvObject as { key: string; value: (PromptId | PromptList)[] }, index)
          ) : (
            <ParamStringInteger
              {...props}
              configuration={{
                type: 'variable',
                value: isVariable(kvObject.value) ? kvObject.value.name : kvObject.value?.toString() || '',
                label: `Value ${index + 1}`,
                required: true,
                subtype: props.configuration?.subtype || 'any'
              }}
              initialValue={kvObject.value}
              onChange={(...args) => {
                const newInitialValue = [...initialValue];
                args.forEach(([, newValue]) => {
                  newInitialValue[index].value = newValue as any;
                });
                props.onChange([props.configuration.value, newInitialValue]);
              }}
            />
          )}
          <ListArrows
            data={initialValue}
            index={index}
            onChange={(result) => props.onChange([props.configuration.value, result])}
          />
          <ConfirmPopover
            onConfirm={() => removeEntry(index, initialValue)}
            icon={<Clear />}
            skipConfirm={!kvObject.key}
            text={
              <>
                Remove <code>{kvObject.key}</code> from list?
              </>
            }
          />
        </ListItem>
      ))}
      <ListItem>
        <Button
          variant='contained'
          color='primary'
          onClick={() => {
            const value = configuration.subtype === 'prompts' ? [] : undefined;
            props.onChange([
              configuration.value,
              initialValue ? [...initialValue, { key: '', value: value }] : [{ key: '', value: value }]
            ]);
          }}
        >
          Add variable
        </Button>
      </ListItem>
      <PromptListPromptPickerDialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        onSave={(newPrompt) => {
          if (!initialValue) return;
          if (configuration.subtype === 'prompts') {
            const newValue = (initialValue[currentIndex].value || []) as (PromptId | PromptList)[];
            newValue.push(isNumber(newPrompt) ? newPrompt : { name: newPrompt.name, scope: newPrompt.scope });
            initialValue[currentIndex].value = newValue;
            props.onChange([props.configuration.value, initialValue]);
          } else {
            initialValue[currentIndex].value = isNumber(newPrompt)
              ? newPrompt
              : { name: newPrompt.name, scope: newPrompt.scope };
            props.onChange([props.configuration.value, initialValue]);
          }
        }}
        subroutineId={props.parentId}
      />
    </List>
  );
};

export default ParamKvValueList;
